import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import Button from './common/Button';
import { LoadingState } from './common/Spinner';

const ImportBrandsModal = ({ isOpen, onClose, onSuccess }) => {
  const toast = useToast();
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [parsedBrands, setParsedBrands] = useState([]);
  const [previewError, setPreviewError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      setFile(null);
      setParsedBrands([]);
      setPreviewError('');
    }
  }, [isOpen]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFile(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      processFile(files[0]);
    }
  };

  const processFile = (selectedFile) => {
    if (!selectedFile.name.endsWith('.csv')) {
      setPreviewError('Invalid file type. Please upload a CSV file (.csv)');
      setFile(null);
      setParsedBrands([]);
      return;
    }

    setFile(selectedFile);
    setPreviewError('');

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const parsed = parseCSV(text);
        if (parsed.length === 0) {
          setPreviewError('No valid data found in CSV file.');
          setParsedBrands([]);
        } else {
          setParsedBrands(parsed);
        }
      } catch (err) {
        console.error('Error parsing file:', err);
        setPreviewError('Error reading or parsing CSV file.');
        setParsedBrands([]);
      }
    };
    reader.readAsText(selectedFile);
  };

  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/);
    if (lines.length === 0) return [];
    
    // Find the header line (skip blank lines at top)
    let headerIndex = -1;
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].trim()) {
        headerIndex = i;
        break;
      }
    }

    if (headerIndex === -1) return [];

    // Parse headers, replace quotes and trim
    const headers = lines[headerIndex]
      .split(',')
      .map(h => h.replace(/^"|"$/g, '').trim().toLowerCase());

    const result = [];
    for (let i = headerIndex + 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      // Split keeping quoted fields intact
      const row = [];
      let currentVal = '';
      let insideQuote = false;

      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        if (char === '"') {
          insideQuote = !insideQuote;
        } else if (char === ',' && !insideQuote) {
          row.push(currentVal.trim());
          currentVal = '';
        } else {
          currentVal += char;
        }
      }
      row.push(currentVal.trim());

      if (row.length === 0 || (row.length === 1 && !row[0])) continue;

      const item = {};
      headers.forEach((header, index) => {
        const value = row[index] || '';
        const cleanedValue = value.replace(/^"|"$/g, '').trim();

        // Flexibly map headers to keys
        if (header.includes('category') || header === 'type') {
          item.category = cleanedValue.toUpperCase();
        } else if (header.includes('brand') || header === 'name' || header === 'title') {
          item.name = cleanedValue;
        } else if (header === 'size' || header.includes('ml') || header.includes('volume') || header.includes('ltr')) {
          item.size = cleanedValue;
        } else if (header.includes('package') || header === 'packaging') {
          item.packageType = cleanedValue.toUpperCase();
        } else if (header.includes('opening') || header.includes('ob') || header.includes('default')) {
          item.defaultOb = parseInt(cleanedValue, 10) || 0;
        } else if (header.includes('invoice')) {
          item.invoicePrice = parseFloat(cleanedValue) || 0;
        } else if (header.includes('mrp')) {
          item.mrpPrice = parseFloat(cleanedValue) || 0;
        } else if (header.includes('selling') || header === 'price') {
          item.sellingPrice = parseFloat(cleanedValue) || 0;
        }
      });

      // Ensure required properties exist, else try indices fallback if headers are not standard
      if (!item.name && row[0]) item.name = row[0];
      if (!item.category && row[1]) item.category = row[1].toUpperCase();
      if (!item.size && row[2]) item.size = row[2];

      if (item.name && item.category) {
        result.push(item);
      }
    }

    return result;
  };

  const downloadSampleCSV = () => {
    const csvContent = 
      "Category, Brand Name, Size, Packaging, Opening Balance, Invoice Price, MRP Price, Selling Price\n" +
      "WHISKY, McDowells No.1, 750ml, BTL, 12, 110.00, 150.00, 140.00\n";
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "BizTracker_Sample_Brands.csv");
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImport = async () => {
    if (parsedBrands.length === 0) return;

    setSubmitting(true);
    try {
      const response = await api.post('/stock/brands/import', { brands: parsedBrands });
      toast.success(response.data.message || 'Brands imported successfully!');
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Import error:', error);
      const errMsg = error.response?.data?.error || 'Failed to import brands';
      toast.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Group preview stats by category
  const categorySummary = {};
  parsedBrands.forEach(b => {
    categorySummary[b.category] = (categorySummary[b.category] || 0) + 1;
  });

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="import-brand-modal-title"
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-xl w-full max-h-[90vh] flex flex-col slide-in border border-transparent dark:border-slate-700"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center shrink-0">
          <h2 id="import-brand-modal-title" className="text-xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <i className="fas fa-file-import text-emerald-600 dark:text-emerald-400"></i> Import Brands (Bulk Upload)
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-slate-200 transition-all text-xl font-bold"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Instructions Alert */}
          <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-100 dark:border-emerald-900/50 rounded-xl p-4 text-xs text-emerald-800 dark:text-emerald-300">
            <div className="flex justify-between items-center mb-1">
              <p className="font-bold flex items-center gap-1.5">
                <i className="fas fa-info-circle"></i> CSV Format Guide
              </p>
              <button
                type="button"
                onClick={downloadSampleCSV}
                className="text-emerald-600 dark:text-emerald-450 hover:text-emerald-700 dark:hover:text-emerald-350 hover:underline font-bold flex items-center gap-1 text-[11px]"
              >
                <i className="fas fa-download"></i> Download Sample CSV
              </button>
            </div>
            <p className="mb-2">Your CSV file should contain columns matching these names (casing is ignored):</p>
            <div className="bg-white/80 dark:bg-slate-900/60 p-2.5 rounded-lg font-mono text-[10px] border border-emerald-100/60 text-slate-700 dark:text-slate-300 overflow-x-auto">
              Category, Brand Name, Size, Packaging, Opening Balance, Invoice Price, MRP Price, Selling Price
            </div>
            <p className="mt-2 font-semibold">Example Row:</p>
            <div className="bg-white/80 dark:bg-slate-900/60 p-2.5 rounded-lg font-mono text-[10px] border border-emerald-100/60 text-slate-700 dark:text-slate-300 overflow-x-auto mt-1">
              WHISKY, McDowells No.1, 750ml, BTL, 12, 110.00, 150.00, 140.00
            </div>
          </div>

          {/* Drag and Drop Area */}
          <div
            className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all duration-200 flex flex-col items-center justify-center gap-3 select-none ${
              dragOver
                ? 'border-emerald-500 bg-emerald-500/10 scale-[0.99]'
                : file
                ? 'border-emerald-350 bg-emerald-50/10 dark:border-emerald-800/40'
                : 'border-slate-200 hover:border-slate-350 hover:bg-slate-50 dark:border-slate-700 dark:hover:border-slate-600 dark:hover:bg-slate-900/40'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".csv"
              className="hidden"
            />
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl shadow-sm ${
              file ? 'bg-emerald-500 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-400'
            }`}>
              <i className={file ? 'fas fa-file-csv' : 'fas fa-cloud-upload-alt'}></i>
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800 dark:text-slate-200">
                {file ? file.name : 'Choose file or drag here'}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {file ? `${(file.size / 1024).toFixed(1)} KB` : 'Only CSV files supported'}
              </p>
            </div>
            {!file && (
              <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 hover:underline">
                Browse Files
              </span>
            )}
          </div>

          {/* Preview Panel */}
          {previewError && (
            <div className="bg-rose-50 dark:bg-rose-950/20 border border-rose-100 dark:border-rose-900/50 rounded-xl p-3 text-xs text-rose-600 dark:text-rose-400 font-semibold flex items-center gap-2">
              <i className="fas fa-exclamation-triangle"></i>
              <span>{previewError}</span>
            </div>
          )}

          {parsedBrands.length > 0 && (
            <div className="space-y-4 animate-slide-in">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200">Parsed Summary</h3>
                <span className="text-xs bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border border-emerald-100 px-2 py-0.5 rounded font-bold font-mono">
                  {parsedBrands.length} brands ready
                </span>
              </div>

              {/* Group Counts Grid */}
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {Object.entries(categorySummary).map(([cat, count]) => (
                  <div key={cat} className="bg-slate-50 dark:bg-slate-900 border border-slate-200/60 dark:border-slate-700 rounded-lg p-2 text-center">
                    <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 block truncate">{cat}</span>
                    <span className="text-sm font-extrabold text-slate-800 dark:text-slate-200">{count}</span>
                  </div>
                ))}
              </div>

              {/* Data Table Preview */}
              <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
                <table className="w-full text-xs text-left border-collapse">
                  <thead className="bg-slate-50 dark:bg-slate-900 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700 sticky top-0">
                    <tr>
                      <th className="p-2.5 font-bold">Category</th>
                      <th className="p-2.5 font-bold">Brand Name</th>
                      <th className="p-2.5 font-bold">Size</th>
                      <th className="p-2.5 font-bold text-right">OB</th>
                      <th className="p-2.5 font-bold text-right">Price</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-750">
                    {parsedBrands.slice(0, 5).map((b, idx) => (
                      <tr key={idx} className="text-slate-700 dark:text-slate-300">
                        <td className="p-2 font-medium truncate max-w-[80px]">{b.category}</td>
                        <td className="p-2 font-semibold truncate max-w-[120px]">{b.name}</td>
                        <td className="p-2 truncate max-w-[60px] font-mono">{b.size}</td>
                        <td className="p-2 text-right font-mono">{b.defaultOb || 0}</td>
                        <td className="p-2 text-right font-mono">₹{b.sellingPrice?.toFixed(2) || '0.00'}</td>
                      </tr>
                    ))}
                    {parsedBrands.length > 5 && (
                      <tr>
                        <td colSpan="5" className="p-2 text-center text-slate-400 bg-slate-50/50 italic text-[11px]">
                          + {parsedBrands.length - 5} more brands in file...
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3 shrink-0">
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            loading={submitting}
            disabled={submitting || parsedBrands.length === 0}
            className="!bg-emerald-600 hover:!bg-emerald-750 text-white"
          >
            {submitting ? 'Importing...' : `Import ${parsedBrands.length || ''} Brand(s)`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ImportBrandsModal;
